import { useState } from 'react';
import type { TFunction } from 'i18next';

import type { AnnouncementChannel, CreateAnnouncementPayload } from './api';

interface BuildResult {
  payload?: CreateAnnouncementPayload;
  error?: string;
}

/** Local form state + validation for the announcement compose drawer. */
export function useAnnouncementForm(t: TFunction<'adminAnnouncements'>) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<'all' | 'specific'>('all');
  const [tenantIds, setTenantIds] = useState('');
  const [channels, setChannels] = useState<AnnouncementChannel[]>(['in_app']);
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setSubject('');
    setBody('');
    setAudience('all');
    setTenantIds('');
    setChannels(['in_app']);
    setError(null);
  }

  function toggleChannel(channel: AnnouncementChannel): void {
    setChannels((cur) =>
      cur.includes(channel)
        ? cur.filter((c) => c !== channel)
        : [...cur, channel],
    );
  }

  function buildPayload(): BuildResult {
    if (!subject.trim() || !body.trim()) {
      return { error: t('compose.errors.required') };
    }
    if (channels.length === 0) {
      return { error: t('compose.errors.channels_required') };
    }
    const payload: CreateAnnouncementPayload = {
      subject: subject.trim(),
      body: body.trim(),
      audience_kind: audience,
      channels,
    };
    if (audience === 'specific') {
      const ids = tenantIds
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (ids.length === 0) {
        return { error: t('compose.errors.tenants_required') };
      }
      payload.audience_tenant_ids = ids;
    }
    return { payload };
  }

  return {
    subject,
    setSubject,
    body,
    setBody,
    audience,
    setAudience,
    tenantIds,
    setTenantIds,
    channels,
    toggleChannel,
    error,
    setError,
    reset,
    buildPayload,
  };
}
